import { useState } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Ticket, Check, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PromoCodeInput({ 
  subtotal, 
  tripSlug, 
  onApply, 
  onRemove,
  appliedPromo 
}) {
  const [code, setCode] = useState('');
  const [validating, setValidating] = useState(false);

  const handleValidate = async () => {
    if (!code.trim()) {
      toast.error('Please enter a promo code');
      return;
    }

    setValidating(true);
    try {
      const res = await axios.post(`${API}/promo/validate`, {
        code: code.toUpperCase().trim(),
        trip_slug: tripSlug,
        subtotal: subtotal
      });

      if (res.data.valid) {
        onApply({
          code: res.data.code,
          description: res.data.description,
          discount_type: res.data.discount_type,
          discount_value: res.data.discount_value,
          discount_amount: res.data.discount_amount
        });
        toast.success(`Promo code applied! You saved $${res.data.discount_amount.toLocaleString()}`);
        setCode('');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Invalid promo code';
      toast.error(message);
    } finally {
      setValidating(false);
    }
  };

  const handleRemove = () => {
    onRemove();
    toast.info('Promo code removed');
  };

  if (appliedPromo) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-testid="promo-applied">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <Check className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <code className="font-mono font-bold text-green-800">{appliedPromo.code}</code>
                <Badge className="bg-green-600 text-white text-xs">Applied</Badge>
              </div>
              {appliedPromo.description && (
                <p className="text-sm text-green-700">{appliedPromo.description}</p>
              )}
            </div>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-green-600">
              -${appliedPromo.discount_amount?.toLocaleString()}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemove}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 h-auto py-1 px-2"
            >
              <X className="h-3 w-3 mr-1" />
              Remove
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="promo-input">
      <div className="flex items-center gap-2">
        <Ticket className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Have a promo code?</span>
      </div>
      <div className="flex gap-2">
        <Input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Enter code"
          className="font-mono uppercase"
          onKeyDown={(e) => e.key === 'Enter' && handleValidate()}
          data-testid="promo-code-input"
        />
        <Button
          onClick={handleValidate}
          disabled={validating || !code.trim()}
          variant="outline"
          data-testid="apply-promo-btn"
        >
          {validating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            'Apply'
          )}
        </Button>
      </div>
    </div>
  );
}
